import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PopoverProps } from '../../../types/DesignSystem/Popover';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { useDismissable } from '../useDismissable';
import { useMergedRef } from '../useMergedRef';
import { POPOVER_ROOM_PX, fixedAnchorStyle, preferUpward, samePosition, useAnchorUpdates } from '../useAnchorUpdates';
import { withClassName } from '../cn';
import { isAtTabEdge, tabbablesIn } from '../tabbable';

/**
 * Popover (§ 248) — see the type file for when to reach for it.
 *
 * **Non-modal, by contract.** No scrim, no Tab cycle, no page-scroll lock: the page
 * stays live, and Tab walks OUT of the surface into the page — the APG's non-modal
 * dialog. What it does own is the two focus moves a keyboard user needs: on open,
 * focus lands on the first focusable inside (the surface itself, `tabIndex={-1}`,
 * when there is none — so Escape works from a text-only popover); on close, focus
 * returns to the trigger IF it was still inside — never stolen from wherever the user
 * already went.
 *
 * **Chrome is the § 51 popover chrome**, verbatim from Dropdown/Menu — raised surface,
 * hairline edge, `srf` radius, `md` elevation — with the § 242 grammar: rises from its
 * anchor, folds back on exit (`aria-hidden` + inert while leaving), flips to the other
 * side when the viewport runs out, re-measured while open.
 *
 * § 304 — PORTALLED by default (`usePortal`): the surface renders into `document.body`
 * at the fixed place `fixedAnchorStyle` computes from the trigger's rect, re-measured
 * while open, on the `popover` rung. Tab past its last control (Shift+Tab before its
 * first) closes it and hands focus back to the trigger, so the keyboard flow never
 * falls off the end of the document. `usePortal={false}` is the inline-absolute path
 * this shipped with in § 248.
 */

const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  placement = 'bottom-start',
  ariaLabel,
  'aria-label': ariaLabelNative,
  closeOnOutsideClick = true,
  usePortal = true,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, forwardedRef) {
  const surfaceId = `${React.useId()}-popover`;
  const containerRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const setSurfaceRef = useMergedRef<HTMLDivElement>(surfaceRef, forwardedRef);

  const { isPresent, phase } = usePresence(open, MOTION.popover.exitMs);
  const exiting = phase === 'exiting';

  /* The vertical half of `placement` is a preference; `above` is what actually renders. */
  const wantsTop = placement.startsWith('top');
  const atEnd = placement.endsWith('end');
  const [above, setAbove] = useState(wantsTop);
  /* § 304 — the fixed placement of the portalled surface, from the same measurement. */
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const measure = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    let nextAbove: boolean;
    if (wantsTop) {
      // Requested above: flip DOWN only when there is no room above and more below.
      const roomAbove = rect.top;
      const roomBelow = window.innerHeight - rect.bottom;
      nextAbove = !(roomAbove < POPOVER_ROOM_PX && roomBelow > roomAbove);
    } else {
      nextAbove = preferUpward(rect, POPOVER_ROOM_PX);
    }
    setAbove(nextAbove);
    if (usePortal) {
      // § 334 — shift inside the viewport once the surface has a width; cap (and scroll)
      // only a surface taller than the room on its side; hide it when its trigger is
      // scrolled out of view. A surface that fits is placed exactly as before.
      const surface = surfaceRef.current;
      const next = fixedAnchorStyle(rect, {
        above: nextAbove,
        align: atEnd ? 'end' : 'start',
        fit: true,
        // offsetWidth, not the rect: the entry animation scales the box, and a mid-animation
        // rect is narrower than the surface will be.
        floatingWidth: surface?.offsetWidth,
        floatingHeight: surface?.scrollHeight,
        anchorEl: containerRef.current,
      });
      setMenuStyle((prev) => (prev && samePosition(prev, next) ? prev : next));
    }
  }, [wantsTop, atEnd, usePortal]);
  useAnchorUpdates(isPresent && !exiting, containerRef, measure, surfaceRef);
  /* § 334 — the first measure runs before the surface exists; measure again once it has. */
  const surfacePlaced = isPresent && menuStyle !== null;
  React.useLayoutEffect(() => {
    if (surfacePlaced && usePortal) measure();
  }, [surfacePlaced, usePortal, measure]);

  /* A caller may open the surface from state rather than from the trigger's click (the
     trigger's onClick measures before it toggles); the first placement then has to come
     from here, or a portalled surface has nowhere to render. */
  useEffect(() => {
    if (open && usePortal && menuStyle === null) measure();
  }, [open, usePortal, menuStyle, measure]);

  useDismissable({
    isOpen: open,
    onDismiss: () => onOpenChange(false),
    /* § 304 — the portalled surface is not a DOM descendant of the trigger's box. */
    isInside: (target) => !!containerRef.current?.contains(target) || !!surfaceRef.current?.contains(target),
    closeOnOutsidePointerDown: closeOnOutsideClick,
  });

  /*
    § 304 — Tab OUT of a portalled surface closes it and (through the focus-back effect
    below) returns focus to the trigger, so the next Tab moves on from where the user
    was. Left to the browser, Tab from a surface parked at the end of <body> lands on
    the browser chrome. Only the two leaving edges are intercepted; Tab between the
    surface's own controls is the browser's.
  */
  const handleSurfaceKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    /* The caller's own handler (from `rest`) runs first and may cancel the leave. */
    rest.onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== 'Tab' || !usePortal) return;
    const surface = surfaceRef.current;
    if (!surface) return;
    /* § 362 — the edges are the surface's TAB STOPS (`tabbable.ts`). A trailing NumberField's
       steppers or Combobox toggle (`tabindex="-1"`) used to be "the last item", so Tab from
       the real last stop was not seen as leaving and fell out of the portalled surface. */
    const direction = event.shiftKey ? 'backward' : 'forward';
    const active = document.activeElement;
    if (!(active instanceof Element) || isAtTabEdge(tabbablesIn(surface, direction), active, direction)) {
      event.preventDefault();
      onOpenChange(false);
    }
  };

  /* § 304 — a portalled surface waits for its first measurement; until then it is not
     in the DOM even while `isPresent`. Everything that needs the surface keys on this. */
  const surfaceMounted = isPresent && (!usePortal || menuStyle !== null);

  /* Focus IN — keyed on `surfaceMounted` (the surface is in the DOM on that commit; on
     the `open` commit it is not yet mounted, because usePresence defers by one render). */
  const returnFocusTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!surfaceMounted || !open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    const surface = surfaceRef.current;
    if (!surface) return;
    const [first] = tabbablesIn(surface);
    (first ?? surface).focus();
  }, [surfaceMounted, open]);

  /* Focus BACK — only if the user is still inside; a focus that already moved on is theirs. */
  useEffect(() => {
    if (open) return;
    if (surfaceRef.current?.contains(document.activeElement)) {
      returnFocusTo.current?.focus?.();
    }
  }, [open]);

  const triggerEl = React.cloneElement(trigger, {
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    'aria-controls': open ? surfaceId : undefined,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event);
      if (event.defaultPrevented) return;
      if (!open) measure();
      onOpenChange(!open);
    },
  });

  const side = above ? 'tw-bottom-full tw-mb-1' : 'tw-top-full tw-mt-1';
  const align = atEnd ? 'tw-right-0' : 'tw-left-0';
  const origin = above
    ? (atEnd ? 'tw-origin-bottom-right' : 'tw-origin-bottom-left')
    : (atEnd ? 'tw-origin-top-right' : 'tw-origin-top-left');
  const rendered = `${above ? 'top' : 'bottom'}-${atEnd ? 'end' : 'start'}`;

  const surface = (
    <div
      /* rest first — the Button.tsx rule; role/id/aria below are the component's. */
      {...rest}
      ref={setSurfaceRef}
      id={surfaceId}
      role="dialog"
      /* § 354 — the native spelling wins over the deprecated `ariaLabel`. */
      aria-label={ariaLabelNative ?? ariaLabel}
      aria-hidden={exiting || undefined}
      /* Focusable by script only (the no-focusable-content case); `outline-none`
         because the surface is not operable — the Dialog reasoning. */
      tabIndex={-1}
      onKeyDown={handleSurfaceKeyDown}
      data-placement={rendered}
      /* § 304 — the marker Dialog's outside-click guard and the modal Tab cycle read. */
      data-dropdown-portal={usePortal ? surfaceId : undefined}
      style={usePortal ? menuStyle ?? undefined : undefined}
      data-testid={dataTestId}
      className={withClassName(`${usePortal ? '' : `tw-absolute tw-z-overlay ${side} ${align}`} tw-min-w-[240px] tw-max-w-sm ${origin} tw-bg-surface-raised tw-border tw-border-edge tw-rounded-srf tw-shadow-ds-md tw-p-4 tw-text-ink tw-outline-none ${
        exiting ? `${MOTION.popover.exit} tw-pointer-events-none` : MOTION.popover.enter
      }`, className)}
    >
      {children}
    </div>
  );

  return (
    <div ref={containerRef} className="tw-relative tw-inline-block">
      {triggerEl}
      {surfaceMounted && (usePortal ? createPortal(surface, document.body) : surface)}
    </div>
  );
});

export default Popover;
